import { FormsApiClient } from './api';
import { canShow, recordConverted, recordShown } from './frequencyCap';
import { canRender, renderByDisplayType } from './render/index';
import { matchesTargeting } from './targeting';
import { armTrigger } from './trigger';
import type { CmsLeadFormsOptions, LeadFormPublicConfig } from './types';
import { pickVariant } from './variantBucketing';

async function activateForm(client: FormsApiClient, form: LeadFormPublicConfig, visitorCountry: string | null): Promise<void> {
  if (!canRender(form)) return;
  if (!matchesTargeting(form.targeting, visitorCountry)) return;
  if (!canShow(form.id, form.frequencyCap)) return;

  const variant = pickVariant(form.id, form.variants);

  const cleanup = armTrigger(form.trigger, () => {
    cleanup();
    recordShown(form.id);
    client.sendImpression(form.slug, variant?.id ?? null);

    renderByDisplayType(form, client, async (data) => {
      const ok = await client.submit(form.slug, data, variant?.id ?? null);
      if (ok) recordConverted(form.id);
      return ok;
    });
  });
}

async function init(options: CmsLeadFormsOptions): Promise<void> {
  const apiBaseUrl = options.apiBaseUrl ?? '';
  const client = new FormsApiClient(apiBaseUrl, options.projectSlug);
  const { forms, visitorCountry } = await client.fetchActiveForms();
  await Promise.all(forms.map((form) => activateForm(client, form, visitorCountry)));
}

function autoInitFromScriptTag(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  const projectSlug = script?.dataset.project;
  if (!projectSlug) return;
  void init({ projectSlug, apiBaseUrl: script?.dataset.apiBaseUrl });
}

// Auto-init when embedded via a plain <script data-project="..."> tag; bundler consumers of the
// ESM build call `CmsLeadForms.init(...)` explicitly instead.
autoInitFromScriptTag();

export { init };
